import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

import { billingService, BillingOfflineError } from '@/services/billing-service';
import { useUserProfileStore } from '@/stores/user-profile-store';

// M58 plan Step 10b — beta tier countdown.
// Compute days remaining (ceiling) so a beta user with 47.5 days left sees "48 days remaining".
const MS_PER_DAY = 1000 * 60 * 60 * 24;

function daysUntil(iso: string, nowMs: number): number {
  const expiresMs = new Date(iso).getTime();
  if (Number.isNaN(expiresMs)) return 0;
  const diffMs = expiresMs - nowMs;
  if (diffMs <= 0) return 0;
  return Math.ceil(diffMs / MS_PER_DAY);
}

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const user = useUserProfileStore((s) => s.user);
  const isPro = useUserProfileStore((s) => s.isPro);
  const refreshSubscription = useUserProfileStore((s) => s.refreshSubscription);

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isLoadingMonthly, setIsLoadingMonthly] = useState(false);
  const [isLoadingAnnual, setIsLoadingAnnual] = useState(false);
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // On mount: always refresh tier from server so the page reflects current subscription state.
  // This is critical after returning from a Stripe checkout redirect.
  useEffect(() => {
    void refreshSubscription();
    const checkoutResult = searchParams.get('checkout');
    if (checkoutResult === 'success') {
      // Clear the query param so a back/forward navigation doesn't re-trigger
      setSearchParams((prev) => {
        prev.delete('checkout');
        return prev;
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  async function handleUpgrade(interval: 'month' | 'year', setLoading: (v: boolean) => void) {
    setLoading(true);
    setError(null);
    try {
      const { url } = await billingService.startCheckout(interval);
      window.location.href = url;
    } catch (err) {
      if (err instanceof BillingOfflineError) {
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Something went wrong');
      }
      setLoading(false);
    }
  }

  async function handleManageBilling() {
    setIsLoadingPortal(true);
    setError(null);
    try {
      const { url } = await billingService.openPortal();
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setIsLoadingPortal(false);
    }
  }

  const proStatus = isPro() ? user?.subscriptionStatus : null;
  const tierLabel = user?.tier ?? 'starter';

  // M58 plan Step 10b — beta tier surface.
  // Show a "Beta Access" badge + countdown when the user is on the beta tier.
  // The static-per-branch attribute `data-beta-status="active"` is rendered
  // ONLY when tier === 'beta' (and betaExpiresAt is set). When the user is
  // not on beta tier the element is omitted entirely (no `data-beta-status="none"`)
  // so tests can assert presence/absence cleanly.
  // Capture mount-time timestamp once via lazy useState so Date.now() is not called on every
  // re-render. Beta countdown only needs day-level accuracy; mount time is correct.
  // mount-time snapshot; accurate for day-level countdown on typical short-lived page visits
  const [nowMs] = useState<number>(() => Date.now());
  const isBetaUser = user?.tier === 'beta' && user.betaExpiresAt != null;
  const betaDaysRemaining = isBetaUser
    ? daysUntil(user.betaExpiresAt as string, nowMs)
    : 0;

  if (user == null) return null;

  return (
    /*
      (i)  Gate condition verbatim: !(user == null) — equivalently user != null

      (ii) useEffect-alignment interpretation:
           This page has no auth-gated mutation useEffect; useEffects are read-only loaders.
           The gate-met condition ensures data is present before rendering; useEffects that
           depend on the data are unaffected.

      (iii) Layout-neutrality rationale:
            The wrapper uses Tailwind v4's `contents` utility (display: contents) so it sits in
            the DOM for Playwright querying but does not participate in the CSS box tree,
            preserving the inner element's direct-child relationship to its parent and protecting
            iOS Safari PWA viewport-recalc defenses.

      (iv) Optional contract callouts:
           data-status intentionally omitted: gate is data presence, not authStatus.
           See ADR-035 / RULE-194..199.

      (v)  No-gate-page route-ready strategy:
           (a) Page has no natural loading gate — it renders directly from `return (` with
               optional-chained fallbacks rather than an early-return guard.
           (b) Wrapper is conditional on the data-loaded predicate per Shape B locked strategy
               (Option D): `{<predicate> && <div className="contents" data-testid="route-ready">...</div>}`.
           (c) Page returns null during the hydration window — no skeleton introduction.
               While data is absent, the page renders nothing; once the predicate is met, the
               full page renders inside the route-ready wrapper.
           (d) Page-specific UX rationale for not introducing a skeleton:
               SubscriptionPage — subscription state loads synchronously from Zustand on mount;
               the hydration window is negligible and a skeleton would cause unnecessary flicker.
    */
    <div className="contents" data-testid="route-ready">
    <div className="flex flex-col gap-6 py-6 px-4" data-testid="subscription-page">
      {/* Page header — matches More section detail pages */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Go back"
          data-testid="back-button"
          className="flex items-center justify-center -ml-1 p-1 rounded text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
        >
          <ChevronLeft size={20} />
        </button>
        <h1 className="font-display text-display-sm text-[var(--color-text-display)]">Subscription</h1>
      </div>

      {/* Offline guard */}
      {!isOnline && (
        <div
          data-testid="offline-banner"
          className="rounded-lg bg-bark-800 border-[0.5px] border-bark-600 px-4 py-3"
        >
          <p className="text-body-sm text-[var(--color-text-muted)]">
            Billing actions require an internet connection
          </p>
        </div>
      )}

      {/* Billing error banner */}
      {error && (
        <div
          data-testid="billing-error"
          className="bg-ember-500/5 border-l-2 border-ember-500 rounded-lg p-4"
        >
          <p className="text-body-sm text-ember-400">{error}</p>
        </div>
      )}

      {isPro() ? (
        <div className="flex flex-col gap-6">
          {/* CURRENT PLAN section */}
          <div className="flex flex-col gap-3">
            <p className="field-label">Current Plan</p>
            <div className="bg-bark-800 border-[0.5px] border-bark-600 rounded-lg p-4 shadow-card flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <span className="font-display text-display-sm text-[var(--color-text-display)] capitalize">
                  {tierLabel}
                </span>
                {/* M58 plan Step 10b: static-per-branch beta badge.
                    `data-beta-status="active"` appears ONLY when the user is
                    on the beta tier with a non-null betaExpiresAt. */}
                {isBetaUser ? (
                  <span
                    data-beta-status="active"
                    data-testid="beta-badge"
                    className="text-body-xs font-semibold uppercase tracking-wider text-ember-400 bg-ember-900/30 px-2 py-0.5 rounded"
                  >
                    Beta Access
                  </span>
                ) : (
                  <span className="text-body-xs font-semibold uppercase tracking-wider text-ember-400 bg-ember-900/30 px-2 py-0.5 rounded">
                    Pro
                  </span>
                )}
              </div>
              {/* Beta countdown \u2014 surfaces days remaining and the conversion note. */}
              {isBetaUser ? (
                <p
                  data-testid="beta-countdown"
                  className="text-body-sm text-[var(--color-text-muted)]"
                >
                  {betaDaysRemaining === 1
                    ? '1 day remaining'
                    : `${betaDaysRemaining} days remaining`}
                  {' \u2014 '}
                  beta access converts to Starter when it expires.
                </p>
              ) : (
                proStatus && (
                  <p className="text-body-sm text-[var(--color-text-muted)] capitalize">
                    {proStatus.replace('_', ' ')}
                  </p>
                )
              )}
            </div>
          </div>

          {/* MANAGE SUBSCRIPTION section.
              Beta users see "Upgrade to Pro" (Stripe checkout) instead of
              "Manage Billing" \u2014 they have no Stripe subscription to manage. */}
          <div className="flex flex-col gap-3">
            <p className="field-label">
              {isBetaUser ? 'Upgrade to Pro' : 'Manage Subscription'}
            </p>
            <div className="bg-bark-800 border-[0.5px] border-bark-600 rounded-lg p-4 shadow-card">
              {isBetaUser ? (
                <div className="flex flex-col gap-3">
                  <p className="text-body-sm text-[var(--color-text-muted)]">
                    Lock in Pro before your beta access converts to Starter.
                  </p>
                  <button
                    type="button"
                    data-testid="beta-upgrade-monthly-button"
                    disabled={isLoadingMonthly || !isOnline}
                    onClick={() => void handleUpgrade('month', setIsLoadingMonthly)}
                    className="w-full bg-ember-500 text-white rounded-lg py-3 text-body-md font-semibold transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoadingMonthly ? 'Loading\u2026' : 'Go Pro (monthly)'}
                  </button>
                  <button
                    type="button"
                    data-testid="beta-upgrade-annual-button"
                    disabled={isLoadingAnnual || !isOnline}
                    onClick={() => void handleUpgrade('year', setIsLoadingAnnual)}
                    className="w-full bg-ember-500/20 border border-ember-500 text-ember-400 rounded-lg py-3 text-body-md font-semibold transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoadingAnnual ? 'Loading\u2026' : 'Go Pro (annual \u2014 save 33%)'}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  data-testid="manage-billing-button"
                  disabled={isLoadingPortal || !isOnline}
                  onClick={() => void handleManageBilling()}
                  className="w-full bg-ember-500 text-white rounded-lg py-3 text-body-md font-semibold transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoadingPortal ? 'Loading\u2026' : 'Manage Billing'}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {/* CURRENT PLAN section */}
          <div className="flex flex-col gap-3">
            <p className="field-label">Current Plan</p>
            <div className="bg-bark-800 border-[0.5px] border-bark-600 rounded-lg p-4 shadow-card flex flex-col gap-2">
              <span className="font-display text-display-sm text-[var(--color-text-display)]">Starter</span>
              <p className="text-body-sm text-[var(--color-text-muted)]">Free forever</p>
            </div>
          </div>

          {/* UPGRADE TO PRO section */}
          <div className="flex flex-col gap-3">
            <p className="field-label">Upgrade to Pro</p>

            {/* Monthly card */}
            <div className="bg-bark-800 border-[0.5px] border-bark-600 rounded-lg p-4 shadow-card flex flex-col gap-3">
              <div className="flex flex-col gap-0.5">
                <span className="font-display text-display-sm text-[var(--color-text-display)]">Monthly</span>
                <p className="text-body-sm text-[var(--color-text-muted)]">$4.99 / month</p>
              </div>
              <button
                type="button"
                data-testid="upgrade-monthly-button"
                disabled={isLoadingMonthly || !isOnline}
                onClick={() => void handleUpgrade('month', setIsLoadingMonthly)}
                className="bg-ember-500 text-white rounded-lg py-3 w-full text-body-md font-semibold transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoadingMonthly ? 'Loading\u2026' : 'Go Pro (monthly)'}
              </button>
            </div>

            {/* Annual card */}
            <div className="bg-bark-800 border-[0.5px] border-bark-600 rounded-lg p-4 shadow-card flex flex-col gap-3">
              <div className="flex flex-col gap-0.5">
                <span className="font-display text-display-sm text-[var(--color-text-display)]">Annual</span>
                <p className="text-body-sm text-[var(--color-text-muted)]">$39.99 / year — save 33%</p>
              </div>
              <button
                type="button"
                data-testid="upgrade-annual-button"
                disabled={isLoadingAnnual || !isOnline}
                onClick={() => void handleUpgrade('year', setIsLoadingAnnual)}
                className="bg-ember-500 text-white rounded-lg py-3 w-full text-body-md font-semibold transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoadingAnnual ? 'Loading\u2026' : 'Go Pro (annual)'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
